import React, { useState } from 'react';
import { FiX, FiMail, FiFile, FiSend, FiLoader } from 'react-icons/fi';
import { Document, documentsService } from '../services/documents.service';
import { toast } from 'react-hot-toast';

interface EmailDocumentsModalProps {
  documents: Document[];
  propertyName: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const EmailDocumentsModal: React.FC<EmailDocumentsModalProps> = ({
  documents,
  propertyName,
  onClose,
  onSuccess
}) => {
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState(`Documents from ${propertyName}`);
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [shareResult, setShareResult] = useState<{
    shareUrl: string;
    pinCode: string;
  } | null>(null);

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !validateEmail(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSending(true);

    try {
      const result = await documentsService.shareDocuments({
        documentIds: documents.map(d => parseInt(d.id)),
        recipientEmail: email,
        subject,
        message
      });

      if (result.success) {
        setShareResult({
          shareUrl: result.shareUrl,
          pinCode: result.pinCode
        });
        toast.success('Email sent successfully!');
      } else {
        toast.error(result.message || 'Failed to send email');
      }
    } catch (error: any) {
      console.error('Error sharing documents:', error);
      toast.error(error.response?.data?.detail || 'Failed to share documents');
    } finally {
      setIsSending(false);
    }
  };

  const totalSize = documents.reduce((sum, d) => sum + (d.file_size || 0), 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <FiMail className="w-5 h-5 text-indigo-400" />
            {shareResult ? 'Documents Shared!' : 'Email Documents'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {shareResult ? (
            // Success view
            <div className="text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiMail className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">
                Documents have been shared!
              </h3>
              <p className="text-gray-400 mb-6">
                An email has been sent to {email} with download instructions.
              </p>

              <div className="bg-gray-900 rounded-lg p-4 mb-6 text-left">
                <div className="mb-4">
                  <label className="text-gray-400 text-sm">Share Link:</label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="text"
                      value={shareResult.shareUrl}
                      readOnly
                      className="flex-1 bg-gray-700 text-white px-3 py-2 rounded text-sm"
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(shareResult.shareUrl);
                        toast.success('Link copied!');
                      }}
                      className="px-3 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-sm"
                    >
                      Copy
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-gray-400 text-sm">PIN Code:</label>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-2xl font-mono font-bold text-indigo-400 tracking-widest">
                      {shareResult.pinCode}
                    </span>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(shareResult.pinCode);
                        toast.success('PIN copied!');
                      }}
                      className="px-3 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 text-sm"
                    >
                      Copy
                    </button>
                  </div>
                </div>
              </div>

              <p className="text-gray-500 text-sm mb-4">
                The recipient will need both the link and PIN to download the documents.
                This link expires in 4 days.
              </p>

              <button
                onClick={onSuccess}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Done
              </button>
            </div>
          ) : (
            // Form view
            <form onSubmit={handleSubmit}>
              {/* Documents list */}
              <div className="mb-6">
                <h3 className="text-gray-400 text-sm font-medium mb-2">
                  Documents to share ({documents.length})
                </h3>
                <div className="bg-gray-900 rounded-lg max-h-40 overflow-y-auto">
                  {documents.map(doc => (
                    <div
                      key={doc.id}
                      className="flex items-center gap-3 p-3 border-b border-gray-800 last:border-0"
                    >
                      <FiFile className="w-4 h-4 text-gray-400" />
                      <span className="text-white text-sm flex-1 truncate">
                        {doc.suggested_copy_destination_name || doc.filename}
                      </span>
                      <span className="text-gray-500 text-xs">
                        {doc.file_size ? `${(doc.file_size / 1024).toFixed(1)} KB` : ''}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-gray-500 text-xs mt-2">
                  Total size: {(totalSize / 1024).toFixed(1)} KB
                </p>
              </div>

              {/* Recipient email */}
              <div className="mb-4">
                <label className="block text-gray-400 text-sm font-medium mb-2">
                  Recipient Email *
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@example.com"
                  className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>

              {/* Subject */}
              <div className="mb-4">
                <label className="block text-gray-400 text-sm font-medium mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Subject line"
                  className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Message */}
              <div className="mb-6">
                <label className="block text-gray-400 text-sm font-medium mb-2">
                  Message (optional)
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Add a personal message..."
                  rows={3}
                  className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                />
              </div>

              {/* Info */}
              <div className="bg-indigo-900/30 border border-indigo-500/30 rounded-lg p-4 mb-6">
                <p className="text-indigo-300 text-sm">
                  The recipient will receive an email with a secure download link and PIN code.
                  The link expires in 4 days.
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSending || !email}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg transition-colors ${
                    isSending || !email
                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700'
                  }`}
                >
                  {isSending ? (
                    <>
                      <FiLoader className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <FiSend className="w-4 h-4" />
                      Send Email
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
